import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../api/apiClient';
import { type MeResponse, UnauthorizedError } from '../api/types';
import { clearPriorSession, hadPriorSession, markPriorSession } from './priorSessionMarker';
import {
    SessionContext,
    type SessionContextValue,
    type SessionState,
    type SessionStatus,
} from './sessionContext';

export interface SessionProviderProps {
    readonly children: ReactNode;
}

/** Build the authenticated state from a `/me` body — the held-key SET is what `hasPermission` reads. */
function authenticatedState(me: MeResponse): SessionState {
    const held = new Set(me.permissions.map((permission) => permission.key));
    return {
        status: 'authenticated',
        user: {
            id: me.user.id,
            displayName: me.user.displayName,
            email: me.user.email,
            managerId: me.user.managerId,
        },
        role: me.role,
        roleName: me.roleName,
        permissions: me.permissions.map((permission) => ({ key: permission.key })),
        hasPermission: (key) => held.has(key),
    };
}

/**
 * Settle the store to `unauthenticated` on a 401, distinguishing an involuntary expiry from a first
 * visit (BUG-007). It is an expiry when the store was still `authenticated` (a mid-session 401) OR
 * this browser recorded a prior session a reload has since lost (the only user-observable path today,
 * carried by [[priorSessionMarker]]) — `LoginRoute` shows its notice for `reason: 'expired'` only. A
 * first-visit / never-authenticated 401 leaves `reason` unset and stays silent. A voluntary sign-out
 * clears the marker and sets its own `reason: 'signout'`, so it never resolves here as an expiry.
 */
function settleUnauthenticated(previous: SessionState): SessionState {
    if (previous.status === 'authenticated' || hadPriorSession()) {
        return { status: 'unauthenticated', reason: 'expired' };
    }
    return { status: 'unauthenticated' };
}

/**
 * Runs the session bootstrap once at the app root and publishes the store below it.
 *
 * On mount it calls `GET /api/v1/me` through the shared `apiClient`: a `200` populates the store, a
 * `401` (thrown as `UnauthorizedError`) marks it `unauthenticated`. It also registers the client's
 * global `onUnauthorized` handler, so a `401` from ANY later call — not just the bootstrap — flips
 * the store to `unauthenticated`; the route guard (`src/app/AuthenticatedGate`) is what turns that
 * flip into a navigate, which keeps this module a `src/lib` leaf (F5-a Decision 7).
 *
 * **The background re-read (F5-k FR-19).** While the store is `authenticated`, it re-reads `/me`
 * after a `403` from any call (the client's `onForbidden` seam — the status alone, a CSRF refusal
 * included), when the window gains focus, and when the document becomes visible, because an
 * administrator may have changed the user's permissions while they were signed in (AD #34). Concurrent
 * triggers share one request in flight. A `200` replaces the permission set, so the navigation, the
 * route guards and every `PermissionGuard` re-render from it; a `401` takes the expiry path, as any
 * other `401` does; any other failure leaves the store as it is — a background re-read never signs the
 * user out. The re-read only ever updates an `authenticated` store, so one that resolves after a
 * sign-out or an expiry does not restore the session. `refresh` stays separate and uncoalesced: a
 * sign-in needs a request issued after its new session cookie exists.
 *
 * **The query cache ends with the session (BUG-083, BUG-085).** Every feature read is cached in the
 * app's one TanStack `QueryClient`, so this provider clears it when the session ends (a confirmed
 * sign-out, or a `401`) and when a `/me` read names a different user than the one on screen (a stub
 * sign-in in the same tab, or a re-read after another tab signed in as someone else). It clears
 * BEFORE it publishes the new state, so no render pairs the new session with the old session's reads;
 * a `/me` read for the same user keeps the cache. It needs a `QueryClientProvider` above it.
 *
 * Otherwise thin like `PermissionProvider`: no caching, no retry policy.
 */
export function SessionProvider({ children }: SessionProviderProps) {
    const [state, setState] = useState<SessionState>({ status: 'loading' });
    const queryClient = useQueryClient();
    // Read by the re-read triggers, which live outside render: the latest status, and the one re-read
    // in flight (shared by every trigger that fires while it is pending).
    const statusRef = useRef<SessionStatus>('loading');
    const reReadInFlight = useRef<Promise<void> | undefined>(undefined);
    // The id of the user on screen, or undefined when no session is; compared with the next `/me`.
    const userIdRef = useRef<number | undefined>(undefined);
    // Bumped by every sign-in (`refresh` succeeding) and every confirmed sign-out (the logout
    // succeeded or answered 401; a refused logout leaves the session live, BUG-052), so a re-read issued for an
    // earlier session never writes over the session that replaced it. Every bump also forgets the
    // in-flight re-read, so a trigger in the new session sends its own rather than joining one whose
    // result will be discarded.
    const sessionGeneration = useRef(0);

    // A layout effect, not a passive one: it runs during the commit, so a trigger never reads a status
    // older than the one already on screen.
    const userId = state.status === 'authenticated' ? state.user.id : undefined;
    useLayoutEffect(() => {
        statusRef.current = state.status;
        userIdRef.current = userId;
    }, [state.status, userId]);

    // Called just before a state is published: with no id when the session is ending, with the next
    // user's id after a `/me` read. Clears the cache unless that id is the one already on screen.
    const clearCacheUnlessSameUser = useCallback(
        (nextUserId?: number) => {
            if (nextUserId === undefined || nextUserId !== userIdRef.current) {
                queryClient.clear();
            }
        },
        [queryClient],
    );

    const refresh = useCallback(async () => {
        try {
            const me = await apiClient.get<MeResponse>('/api/v1/me');
            // Record that this browser now holds a session, so a later reload that loses it can tell
            // the resulting cold-boot 401 from a genuine first visit (BUG-007).
            markPriorSession();
            sessionGeneration.current += 1;
            reReadInFlight.current = undefined;
            clearCacheUnlessSameUser(me.user.id);
            setState(authenticatedState(me));
        } catch (error) {
            if (error instanceof UnauthorizedError) {
                clearCacheUnlessSameUser();
                setState(settleUnauthenticated);
                return;
            }
            throw error;
        }
    }, [clearCacheUnlessSameUser]);

    // The background re-read. Never exported: the triggers below are its only callers.
    const revalidate = useCallback((): Promise<void> => {
        if (statusRef.current !== 'authenticated') {
            return Promise.resolve();
        }
        const pending = reReadInFlight.current;
        if (pending !== undefined) {
            return pending;
        }
        const generation = sessionGeneration.current;
        const reRead = apiClient
            .get<MeResponse>('/api/v1/me')
            .then(
                (me) => {
                    if (generation !== sessionGeneration.current) {
                        return;
                    }
                    // Only an authenticated store takes the result, so only then can the user change.
                    if (statusRef.current === 'authenticated') {
                        clearCacheUnlessSameUser(me.user.id);
                    }
                    setState((previous) =>
                        previous.status === 'authenticated' ? authenticatedState(me) : previous,
                    );
                },
                (error: unknown) => {
                    if (error instanceof UnauthorizedError && generation === sessionGeneration.current) {
                        // The expiry path. The client's own 401 handler has usually settled the store
                        // already; this only settles a store that is still authenticated, so it never
                        // overwrites a sign-out's reason.
                        clearCacheUnlessSameUser();
                        setState((previous) =>
                            previous.status === 'authenticated' ? settleUnauthenticated(previous) : previous,
                        );
                    }
                    // Anything else (a 5xx, the network, a 403) leaves the screen and permissions as they are.
                },
            )
            .finally(() => {
                // A sign-in or sign-out may have forgotten this re-read and a newer one taken its
                // place; clear the record only while it is still this one.
                if (reReadInFlight.current === reRead) {
                    reReadInFlight.current = undefined;
                }
            });
        reReadInFlight.current = reRead;
        return reRead;
    }, [clearCacheUnlessSameUser]);

    const signOut = useCallback(async () => {
        try {
            await apiClient.post('/api/v1/auth/logout');
        } catch (error) {
            // A logout the server did not confirm (network, 5xx, a CSRF 403) leaves the server session
            // live, so the user is NOT shown as signed out: the store, and the prior-session marker,
            // stay as they are and the rejection goes to the caller, which says so (BUG-052). A 401 is
            // the one refusal that proves the session is already gone, so it settles as a sign-out.
            if (!(error instanceof UnauthorizedError)) {
                throw error;
            }
        }
        // Clear the prior-session marker so the next cold-boot 401 reads as a first visit, not an
        // expiry — a deliberate sign-out must stay silent (BUG-007). `reason: 'signout'` marks this as
        // VOLUNTARY, so the gate skips deep-link preservation and re-login lands on the default
        // section, not the page signed out from (BUG-005). Bumping the generation and dropping any
        // in-flight `/me` re-read stops a re-read that started before the sign-out from settling
        // the session back to authenticated.
        clearPriorSession();
        sessionGeneration.current += 1;
        reReadInFlight.current = undefined;
        clearCacheUnlessSameUser();
        setState({ status: 'unauthenticated', reason: 'signout' });
    }, [clearCacheUnlessSameUser]);

    // Register the global 401 handler for the lifetime of the provider. Separate from the bootstrap
    // effect so it is in place before — and independent of — the first `/me` result.
    useEffect(() => {
        apiClient.setUnauthorizedHandler(() => {
            clearCacheUnlessSameUser();
            setState(settleUnauthenticated);
        });
        return () => apiClient.setUnauthorizedHandler(undefined);
    }, [clearCacheUnlessSameUser]);

    // Re-read after a 403 from any call. A 403 answered to the re-read itself arrives while that
    // re-read is still in flight (the client calls the handler before it throws), so it joins it
    // instead of sending a second request.
    useEffect(() => {
        apiClient.setForbiddenHandler(() => {
            void revalidate();
        });
        return () => apiClient.setForbiddenHandler(undefined);
    }, [revalidate]);

    // Re-read when the window gains focus or the document becomes visible. A tab switch fires both;
    // the one-in-flight rule makes them one request.
    useEffect(() => {
        const onFocus = () => {
            void revalidate();
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void revalidate();
            }
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [revalidate]);

    // The bootstrap `/me` call, at the root, regardless of entry route (F5-a Decision 9). StrictMode double-
    // invokes this in dev only; production runs it once, which is what F5-a AC-15's "exactly once" means.
    useEffect(() => {
        // Fail closed (F5-a NFR-2): a non-401 bootstrap failure (network / 5xx) cannot confirm a session, so
        // it settles unauthenticated (→ /login via the gate) rather than leaving an unhandled rejection
        // and an infinite BootstrapGate. `reason: 'unavailable'` lets the login page say the service is
        // down instead of offering a sign-in that cannot work yet. refresh() still re-throws non-401 for
        // StubSignIn's own catch — this .catch is the bootstrap-only handler. No console (noConsole is
        // an error) and src/lib is a leaf that cannot reach the src/app telemetry seam. The failure is kept,
        // so the login page's notice can show its reference (BUG-212).
        void refresh().catch((failedRequest: unknown) =>
            setState({ status: 'unauthenticated', reason: 'unavailable', failedRequest }),
        );
    }, [refresh]);

    const value = useMemo<SessionContextValue>(
        () => ({ ...state, refresh, signOut }),
        [state, refresh, signOut],
    );

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
